/**
 * GalleryFotoKegiatan.jsx
 *
 * Komponen galeri foto kegiatan untuk halaman BeritaDetail.
 * Mendukung dua mode tampilan yang bisa di-toggle oleh user:
 *   - Slider (default): satu foto besar + thumbnail strip + navigasi panah
 *   - Grid: layout mosaic (1 besar + 2 medium + rest kecil)
 *
 * Juga menyertakan Lightbox fullscreen dengan navigasi keyboard.
 *
 * Directus Integration:
 *   Photos harus berupa array of: { id, url, caption }
 *   gunakan `getImageUrl` dari utils/api sebelum passing ke komponen ini.
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Camera, ChevronLeft, ChevronRight, X, Maximize2,
  LayoutGrid, Monitor,
} from 'lucide-react';

/* ═══════════════════════════════════════════
   LIGHTBOX
═══════════════════════════════════════════ */
const Lightbox = ({ photos, index, onChange, onClose }) => {
  const prev = () => onChange((index - 1 + photos.length) % photos.length);
  const next = () => onChange((index + 1) % photos.length);

  const [touchStart, setTouchStart] = useState(null);

  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === 'ArrowLeft') onChange((index - 1 + photos.length) % photos.length);
      if (e.key === 'ArrowRight') onChange((index + 1) % photos.length);
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKey);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', handleKey);
      document.body.style.overflow = '';
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [index]);

  const handleTouchStart = (e) => setTouchStart(e.targetTouches[0].clientX);
  const handleTouchEnd = (e) => {
    if (!touchStart) return;
    const touchEnd = e.changedTouches[0].clientX;
    const diff = touchStart - touchEnd;
    if (diff > 50) next();
    if (diff < -50) prev();
    setTouchStart(null);
  };

  return (
    <div
      onClick={onClose}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
      style={{
        position: 'fixed', inset: 0, zIndex: 9999,
        background: 'rgba(0,0,0,0.96)',
        display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center', gap: 16,
        animation: 'lbFadeIn 0.2s ease',
        touchAction: 'none', // prevent scrolling while swiping lightbox
      }}
    >
      <style>{`
        @keyframes lbFadeIn { from { opacity: 0; } to { opacity: 1; } }
        @keyframes lbImgIn { from { opacity: 0; transform: scale(0.97); } to { opacity: 1; transform: scale(1); } }
      `}</style>

      {/* Close */}
      <button
        onClick={onClose}
        style={{
          position: 'absolute', top: 20, right: 20,
          background: 'rgba(255,255,255,0.1)', border: 'none',
          color: '#fff', width: 40, height: 40, borderRadius: '50%',
          cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
          transition: 'background 0.2s',
        }}
        onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.2)'}
        onMouseLeave={e => e.currentTarget.style.background = 'rgba(255,255,255,0.1)'}
      >
        <X size={18} />
      </button>

      {/* Counter */}
      <div style={{
        position: 'absolute', top: 24, left: '50%', transform: 'translateX(-50%)',
        color: 'rgba(255,255,255,0.6)', fontSize: 13, fontWeight: 500,
      }}>
        {index + 1} / {photos.length}
      </div>

      {/* Main image + arrows — hanya img yang stopPropagation, bukan seluruh row */}
      <div
        style={{
          position: 'relative', display: 'flex', alignItems: 'center', gap: 16,
          maxWidth: '92vw', width: '100%', justifyContent: 'center',
        }}
      >
        <button
          onClick={e => { e.stopPropagation(); onChange((index - 1 + photos.length) % photos.length); }}
          style={{
            background: 'rgba(255,255,255,0.1)', border: 'none', color: '#fff',
            width: 48, height: 48, borderRadius: '50%', cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            flexShrink: 0, transition: 'background 0.2s',
          }}
          onMouseEnter={e => e.currentTarget.style.background = 'rgba(123,47,190,0.8)'}
          onMouseLeave={e => e.currentTarget.style.background = 'rgba(255,255,255,0.1)'}
        >
          <ChevronLeft size={22} />
        </button>

        <img
          key={index}
          src={photos[index].url}
          alt={photos[index].caption || ''}
          onClick={e => e.stopPropagation()}
          style={{
            maxHeight: '72vh', maxWidth: '80vw', objectFit: 'contain',
            borderRadius: 12, boxShadow: '0 32px 80px rgba(0,0,0,0.8)',
            animation: 'lbImgIn 0.25s ease',
            cursor: 'default',
          }}
        />

        <button
          onClick={e => { e.stopPropagation(); onChange((index + 1) % photos.length); }}
          style={{
            background: 'rgba(255,255,255,0.1)', border: 'none', color: '#fff',
            width: 48, height: 48, borderRadius: '50%', cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            flexShrink: 0, transition: 'background 0.2s',
          }}
          onMouseEnter={e => e.currentTarget.style.background = 'rgba(123,47,190,0.8)'}
          onMouseLeave={e => e.currentTarget.style.background = 'rgba(255,255,255,0.1)'}
        >
          <ChevronRight size={22} />
        </button>
      </div>

      {/* Caption */}
      {photos[index].caption && (
        <div style={{ textAlign: 'center', padding: '0 24px' }}>
          <p style={{ color: 'rgba(255,255,255,0.85)', fontSize: 13, fontWeight: 400 }}>
            {photos[index].caption}
          </p>
        </div>
      )}

      {/* Thumbnail strip */}
      <div
        onClick={e => e.stopPropagation()}
        style={{
          display: 'flex', gap: 8, overflowX: 'auto', padding: '0 24px 8px',
          maxWidth: '100vw',
        }}
      >
        {photos.map((p, i) => (
          <button
            key={p.id ?? i}
            onClick={() => onChange(i)}
            style={{
              flexShrink: 0, width: 64, height: 44, borderRadius: 8, overflow: 'hidden',
              border: i === index ? '2px solid #7B2FBE' : '2px solid transparent',
              opacity: i === index ? 1 : 0.45, cursor: 'pointer', padding: 0,
              transition: 'all 0.2s',
            }}
          >
            <img src={p.url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
          </button>
        ))}
      </div>
    </div>
  );
};

/* ═══════════════════════════════════════════
   SLIDER MODE
═══════════════════════════════════════════ */
const SliderView = ({ photos, onLightbox }) => {
  const [cur, setCur] = useState(0);
  const [fading, setFading] = useState(false);
  const [paused, setPaused] = useState(false);
  const [touchStart, setTouchStart] = useState(null);
  const thumbsRef = useRef(null);
  const AUTO_DELAY = 4500; // ms antar slide

  const scrollThumb = useCallback((idx) => {
    if (thumbsRef.current) {
      const el = thumbsRef.current.children[idx];
      if (el) el.scrollIntoView({ inline: 'center', block: 'nearest', behavior: 'smooth' });
    }
  }, []);

  const goTo = useCallback((idx) => {
    if (fading || idx === cur) return;
    setFading(true);
    setTimeout(() => { setCur(idx); setFading(false); }, 280);
    scrollThumb(idx);
  }, [cur, fading, scrollThumb]);

  const prev = (e) => { e?.stopPropagation(); goTo((cur - 1 + photos.length) % photos.length); };
  const next = (e) => { e?.stopPropagation(); goTo((cur + 1) % photos.length); };

  const handleTouchStart = (e) => setTouchStart(e.targetTouches[0].clientX);
  const handleTouchEnd = (e) => {
    if (!touchStart) return;
    const touchEnd = e.changedTouches[0].clientX;
    const diff = touchStart - touchEnd;
    if (diff > 50) next();
    if (diff < -50) prev();
    setTouchStart(null);
  };

  /* ── Auto slide ── */
  useEffect(() => {
    if (paused || photos.length <= 1 || fading) return;
    const timer = setTimeout(() => {
      const nextIdx = (cur + 1) % photos.length;
      setFading(true);
      setTimeout(() => { setCur(nextIdx); setFading(false); scrollThumb(nextIdx); }, 280);
    }, AUTO_DELAY);
    return () => clearTimeout(timer);
  }, [cur, paused, fading, photos.length, scrollThumb]);

  return (
    <>
      {/* Main photo area — rasio 3:2, pause auto slide saat hover */}
      {/* Lightbox akan tampilkan rasio asli file saat diklik */}
      <div
        onClick={() => onLightbox(cur)}
        onMouseEnter={() => setPaused(true)}
        onMouseLeave={() => setPaused(false)}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
        style={{
          position: 'relative', width: '100%', aspectRatio: '3/2',
          cursor: 'zoom-in', overflow: 'hidden', background: '#0a0a0f',
          touchAction: 'pan-y', // allow vertical scroll, block horizontal scroll for swipe
        }}
      >
        <img
          key={cur}
          src={photos[cur].url}
          alt={photos[cur].caption || ''}
          style={{
            width: '100%', height: '100%', objectFit: 'cover', display: 'block',
            transition: 'opacity 0.28s ease',
            opacity: fading ? 0 : 1,
          }}
        />

        {/* Gradient overlay */}
        <div style={{
          position: 'absolute', bottom: 0, left: 0, right: 0, height: '55%',
          background: 'linear-gradient(to top, rgba(10,10,15,0.85) 0%, transparent 100%)',
          pointerEvents: 'none',
        }} />

        {/* Caption */}
        {photos[cur].caption && (
          <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, padding: '16px 18px 14px' }}>
            <p style={{ color: 'rgba(255,255,255,0.9)', fontSize: 12.5, fontWeight: 500, lineHeight: 1.5 }}>
              {photos[cur].caption}
            </p>
          </div>
        )}

        {/* Expand hint */}
        <div style={{
          position: 'absolute', top: 12, right: 12,
          background: 'rgba(0,0,0,0.4)', borderRadius: 8,
          padding: '5px 8px', display: 'flex', alignItems: 'center', gap: 5,
          color: 'rgba(255,255,255,0.75)', fontSize: 11, fontWeight: 500,
          backdropFilter: 'blur(4px)',
        }}>
          <Maximize2 size={12} /> Perbesar
        </div>

        {/* Side nav arrows */}
        <button
          onClick={prev}
          style={{
            position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)',
            width: 40, height: 40, borderRadius: '50%', border: 'none', cursor: 'pointer',
            background: 'rgba(0,0,0,0.45)', color: '#fff',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            backdropFilter: 'blur(4px)', transition: 'background 0.2s, transform 0.15s',
          }}
          onMouseEnter={e => { e.currentTarget.style.background = 'rgba(123,47,190,0.9)'; e.currentTarget.style.transform = 'translateY(-50%) scale(1.1)'; }}
          onMouseLeave={e => { e.currentTarget.style.background = 'rgba(0,0,0,0.45)'; e.currentTarget.style.transform = 'translateY(-50%)'; }}
        >
          <ChevronLeft size={20} />
        </button>
        <button
          onClick={next}
          style={{
            position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)',
            width: 40, height: 40, borderRadius: '50%', border: 'none', cursor: 'pointer',
            background: 'rgba(0,0,0,0.45)', color: '#fff',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            backdropFilter: 'blur(4px)', transition: 'background 0.2s, transform 0.15s',
          }}
          onMouseEnter={e => { e.currentTarget.style.background = 'rgba(123,47,190,0.9)'; e.currentTarget.style.transform = 'translateY(-50%) scale(1.1)'; }}
          onMouseLeave={e => { e.currentTarget.style.background = 'rgba(0,0,0,0.45)'; e.currentTarget.style.transform = 'translateY(-50%)'; }}
        >
          <ChevronRight size={20} />
        </button>

        {/* Slide indicator dots */}
        <div style={{ position: 'absolute', bottom: 48, right: 14, display: 'flex', gap: 4 }}>
          {photos.map((_, i) => (
            <div key={i} style={{
              width: i === cur ? 18 : 6, height: 6, borderRadius: 3,
              background: i === cur ? '#7B2FBE' : 'rgba(255,255,255,0.35)',
              transition: 'all 0.3s',
            }} />
          ))}
        </div>

        {/* Auto-slide progress bar */}
        {!paused && photos.length > 1 && (
          <div style={{
            position: 'absolute', bottom: 0, left: 0, right: 0, height: 3,
            background: 'rgba(255,255,255,0.15)',
          }}>
            <div
              key={`${cur}-bar`}
              style={{
                height: '100%',
                background: 'linear-gradient(90deg,#7B2FBE,#9333EA)',
                borderRadius: '0 2px 2px 0',
                animation: `slideProgress ${AUTO_DELAY}ms linear forwards`,
              }}
            />
          </div>
        )}
        <style>{`
          @keyframes slideProgress {
            from { width: 0%; }
            to   { width: 100%; }
          }
        `}</style>
      </div>

      {/* Thumbnail strip */}
      <div
        ref={thumbsRef}
        style={{
          display: 'flex', gap: 8, overflowX: 'auto', padding: '12px 14px 10px',
          borderTop: '1px solid #f0eeff',
          scrollbarWidth: 'thin',
          scrollbarColor: 'rgba(123,47,190,0.25) transparent',
        }}
      >
        {photos.map((p, i) => (
          <button
            key={p.id ?? i}
            onClick={() => goTo(i)}
            style={{
              flexShrink: 0, width: 64, height: 64, padding: 0, cursor: 'pointer',
              borderRadius: 10, overflow: 'hidden',
              border: i === cur ? '2.5px solid #7B2FBE' : '2px solid #E5E7EB',
              opacity: i === cur ? 1 : 0.55,
              boxShadow: i === cur ? '0 0 0 3px rgba(123,47,190,0.2)' : 'none',
              transition: 'all 0.2s, transform 0.2s',
            }}
            onMouseEnter={e => { if (i !== cur) e.currentTarget.style.transform = 'scale(1.04)'; }}
            onMouseLeave={e => { e.currentTarget.style.transform = 'scale(1)'; }}
          >
            <img src={p.url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
          </button>
        ))}
      </div>

      {/* Footer */}
      <div style={{
        padding: '10px 16px 14px', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        borderTop: '1px solid #f0eeff',
      }}>
        <span style={{ fontSize: 11.5, color: '#9CA3AF' }}>Klik foto untuk memperbesar</span>
        <button
          onClick={() => onLightbox(0)}
          style={{
            background: 'linear-gradient(135deg,#7B2FBE,#9333EA)',
            color: '#fff', border: 'none', borderRadius: 10,
            padding: '7px 16px', fontSize: 12, fontWeight: 600, cursor: 'pointer',
            display: 'flex', alignItems: 'center', gap: 6, transition: 'opacity 0.2s',
          }}
          onMouseEnter={e => e.currentTarget.style.opacity = '0.88'}
          onMouseLeave={e => e.currentTarget.style.opacity = '1'}
        >
          <LayoutGrid size={13} /> Lihat Semua ({photos.length})
        </button>
      </div>
    </>
  );
};

/* ═══════════════════════════════════════════
   GRID MODE
═══════════════════════════════════════════ */
const GridView = ({ photos, onLightbox }) => {
  const main = photos.slice(0, 1);
  const secondary = photos.slice(1, 3);
  const rest = photos.slice(3);
  const hasMore = photos.length > 6;

  const cellStyle = (aspectRatio = '4/3', extra = {}) => ({
    position: 'relative', borderRadius: 12, overflow: 'hidden',
    aspectRatio, cursor: 'zoom-in', padding: 0, border: 'none',
    display: 'block', width: '100%',
    transition: 'transform 0.2s',
    ...extra,
  });

  const overlayStyle = {
    position: 'absolute', inset: 0,
    background: 'rgba(123,47,190,0.5)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    opacity: 0, transition: 'opacity 0.2s',
    color: '#fff',
  };

  const GridCell = ({ photo, idx, ratio = '4/3', small = false }) => (
    <button
      onClick={() => onLightbox(idx)}
      style={cellStyle(ratio)}
      onMouseEnter={e => {
        e.currentTarget.style.transform = 'scale(1.02)';
        e.currentTarget.querySelector('.grid-overlay').style.opacity = '1';
      }}
      onMouseLeave={e => {
        e.currentTarget.style.transform = 'scale(1)';
        e.currentTarget.querySelector('.grid-overlay').style.opacity = '0';
      }}
    >
      <img
        src={photo.url} alt={photo.caption || ''}
        style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
      />
      <div className="grid-overlay" style={overlayStyle}>
        <Maximize2 size={small ? 14 : 22} />
      </div>
      {photo.caption && !small && (
        <div style={{
          position: 'absolute', bottom: 0, left: 0, right: 0,
          padding: '8px 10px',
          background: 'linear-gradient(to top, rgba(0,0,0,0.7), transparent)',
        }}>
          <p style={{ color: 'rgba(255,255,255,0.9)', fontSize: 11, lineHeight: 1.4 }}>
            {photo.caption}
          </p>
        </div>
      )}
    </button>
  );

  return (
    <div style={{ padding: 12, display: 'flex', flexDirection: 'column', gap: 8 }}>
      {/* Row 1: 1 large + 2 stacked medium */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
        {main.map((p, i) => (
          <GridCell key={p.id ?? i} photo={p} idx={i} ratio="4/3" />
        ))}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {secondary.map((p, i) => (
            <GridCell key={p.id ?? (i + 1)} photo={p} idx={i + 1} ratio="1/1" />
          ))}
        </div>
      </div>

      {/* Row 2: rest (smaller thumbnails) */}
      {rest.length > 0 && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(110px, 1fr))', gap: 8 }}>
          {rest.slice(0, 3).map((p, i) => {
            const isLast = i === 2 && hasMore;
            return (
              <div key={p.id ?? (i + 3)} style={{ position: 'relative', borderRadius: 10, overflow: 'hidden', aspectRatio: '1/1' }}>
                <button
                  onClick={() => onLightbox(i + 3)}
                  style={{ ...cellStyle('1/1'), borderRadius: 10 }}
                  onMouseEnter={e => {
                    e.currentTarget.style.transform = 'scale(1.02)';
                    e.currentTarget.querySelector('.grid-overlay').style.opacity = '1';
                  }}
                  onMouseLeave={e => {
                    e.currentTarget.style.transform = 'scale(1)';
                    e.currentTarget.querySelector('.grid-overlay').style.opacity = '0';
                  }}
                >
                  <img src={p.url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                  <div className="grid-overlay" style={overlayStyle}>
                    <Maximize2 size={14} />
                  </div>
                  {isLast && (
                    <div style={{
                      position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.55)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      color: '#fff', fontSize: 20, fontWeight: 700,
                    }}>
                      +{photos.length - 6}
                    </div>
                  )}
                </button>
              </div>
            );
          })}
        </div>
      )}

      <button
        onClick={() => onLightbox(0)}
        style={{
          alignSelf: 'flex-end', background: 'rgba(123,47,190,0.1)',
          color: '#7B2FBE', border: '1px solid rgba(123,47,190,0.25)',
          borderRadius: 8, padding: '6px 14px', fontSize: 11.5, fontWeight: 600, cursor: 'pointer',
          transition: 'background 0.15s',
        }}
        onMouseEnter={e => e.currentTarget.style.background = 'rgba(123,47,190,0.18)'}
        onMouseLeave={e => e.currentTarget.style.background = 'rgba(123,47,190,0.1)'}
      >
        Lihat Semua ({photos.length}) →
      </button>
    </div>
  );
};

/* ═══════════════════════════════════════════
   MAIN EXPORT: GalleryFotoKegiatan
═══════════════════════════════════════════
 Props:
   photos  — array of { id, url, caption }   (required)
   title   — judul section (default: "Foto Kegiatan")
   defaultMode — 'slider' | 'grid'           (default: 'slider')
═══════════════════════════════════════════ */
const GalleryFotoKegiatan = ({ photos = [], title = 'Foto Kegiatan', defaultMode = 'slider' }) => {
  const [mode, setMode] = useState(defaultMode);
  const [lightbox, setLightbox] = useState(null);

  if (!photos || photos.length === 0) return null;

  const headerCounter = `${photos.length} foto dokumentasi`;

  return (
    <>
      <div style={{
        background: '#ffffff',
        border: '1px solid #e5e7eb',
        borderRadius: 20,
        overflow: 'hidden',
        boxShadow: '0 4px 24px rgba(123,47,190,0.08)',
      }}>
        {/* ── Header ── */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '14px 18px 12px',
          borderBottom: '1px solid #f0eeff',
          background: 'rgba(123,47,190,0.04)',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{
              width: 34, height: 34, borderRadius: 10,
              background: 'linear-gradient(135deg,#7B2FBE,#9333EA)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <Camera size={16} color="#fff" />
            </div>
            <div>
              <p style={{ fontWeight: 700, fontSize: 14, color: '#1A1A2E', lineHeight: 1.2 }}>{title}</p>
              <p style={{ fontSize: 11, color: '#9CA3AF', lineHeight: 1 }}>{headerCounter}</p>
            </div>
          </div>

          {/* Mode toggle buttons */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 4, background: 'rgba(123,47,190,0.06)', borderRadius: 10, padding: 3 }}>
            <button
              onClick={() => setMode('slider')}
              title="Mode Slider"
              style={{
                display: 'flex', alignItems: 'center', gap: 5,
                padding: '5px 10px', borderRadius: 8, border: 'none', cursor: 'pointer',
                background: mode === 'slider' ? '#7B2FBE' : 'transparent',
                color: mode === 'slider' ? '#fff' : '#7B2FBE',
                fontSize: 11.5, fontWeight: 600,
                transition: 'all 0.2s',
              }}
            >
              <Monitor size={13} /> Slide
            </button>
            <button
              onClick={() => setMode('grid')}
              title="Mode Grid"
              style={{
                display: 'flex', alignItems: 'center', gap: 5,
                padding: '5px 10px', borderRadius: 8, border: 'none', cursor: 'pointer',
                background: mode === 'grid' ? '#7B2FBE' : 'transparent',
                color: mode === 'grid' ? '#fff' : '#7B2FBE',
                fontSize: 11.5, fontWeight: 600,
                transition: 'all 0.2s',
              }}
            >
              <LayoutGrid size={13} /> Grid
            </button>
          </div>
        </div>

        {/* ── Content: Slider or Grid ── */}
        {mode === 'slider' ? (
          <SliderView photos={photos} onLightbox={setLightbox} />
        ) : (
          <GridView photos={photos} onLightbox={setLightbox} />
        )}
      </div>

      {/* Lightbox */}
      {lightbox !== null && (
        <Lightbox
          photos={photos}
          index={lightbox}
          onChange={setLightbox}
          onClose={() => setLightbox(null)}
        />
      )}
    </>
  );
};

export default GalleryFotoKegiatan;
